import React from 'react';
import { Check, Clock, Shield, BookOpen, Users, Zap, Award, Download, AlertCircle, TrendingUp, Star, AlertTriangle } from 'lucide-react';
import { MetaPixel } from '../components/MetaPixel';
import { PurchaseButton } from '../components/PurchaseButton';
import { PurchasePopup } from '../components/PurchasePopup';
import { TestimonialCard } from '../components/TestimonialCard';
import { BonusCard } from '../components/BonusCard';
import { FAQItem } from '../components/FAQItem';
import { CountdownTimer } from '../components/CountdownTimer';
import { SocialProofBanner } from '../components/SocialProofBanner';
import { ScarcityIndicator } from '../components/ScarcityIndicator';
import { StickyCtaBar } from '../components/StickyCtaBar';
import { TrustBadges } from '../components/TrustBadges';
import { ValueStackBox } from '../components/ValueStackBox';
export function LandingPage() {
  const mainImage = 'https://cdn.magicpatterns.com/uploads/5LEPBYsLGbF7x4M2smNkhM/PROYECTOS_Anuales_(3).png';
  const bonus1Image = 'https://cdn.magicpatterns.com/uploads/qHBrCiqM9nvBrhtrLn6REz/26.png';
  const bonus2Image = 'https://cdn.magicpatterns.com/uploads/wUuA4ncuVvctCKrn581Cxa/27.png';
  const bonus3Image = 'https://cdn.magicpatterns.com/uploads/2cFsFWRQQZjkXb3n9QHH8o/28.png';
  const bonus4Image = 'https://cdn.magicpatterns.com/uploads/srwiYh6piBLcJ97AZUiAuv/PROYECTOS_Anuales_(4).png';
  const testimonials = [{
    name: 'Laura Fernández',
    role: 'Profesora de Biología',
    location: 'Buenos Aires, Argentina',
    quote: 'Me devolvió el control de mi año docente. Ya no paso los domingos planificando. Ahorré al menos 8 horas semanales.'
  }, {
    name: 'Carlos Méndez',
    role: 'Docente de Secundaria',
    location: 'Córdoba, Argentina',
    quote: 'Nunca tuve un material tan completo. Mis alumnos entienden mucho mejor y participan más. El rendimiento del curso subió un 40%.'
  }, {
    name: 'Ana García',
    role: 'Profesora de Ciencias',
    location: 'Rosario, Argentina',
    quote: 'Mis clases cambiaron por completo. Ahora enseño con orden y tranquilidad. Es la mejor inversión de mi carrera.'
  }, {
    name: 'Roberto Silva',
    role: 'Profesor de Biología',
    location: 'Mendoza, Argentina',
    quote: 'Las guías de laboratorio son espectaculares. Todo está listo para aplicar. Mis alumnos ahora aman las clases prácticas.'
  }, {
    name: 'María Torres',
    role: 'Docente de Ciencias',
    location: 'La Plata, Argentina',
    quote: 'Es la mejor inversión que hice este año. Todo listo para imprimir y usar. La calidad del contenido es superior a cualquier libro.'
  }];
  const faqs = [{
    question: '¿El material es digital o físico?',
    answer: 'Es 100% digital. Recibís acceso inmediato a una plataforma donde podés descargar todo el material en formato PDF de alta calidad, listo para imprimir o usar en dispositivos.'
  }, {
    question: '¿Cómo recibo el MegaPack?',
    answer: 'Inmediatamente después de tu compra, recibirás un correo electrónico con tus datos de acceso a nuestra plataforma privada. Es automático y al instante.'
  }, {
    question: '¿Sirve para todos los niveles?',
    answer: 'Sí, el pack cubre contenidos desde 1° a 6° año de secundaria/bachillerato, con materiales adaptables a diferentes niveles de profundidad.'
  }, {
    question: '¿Necesito experiencia previa?',
    answer: 'No. El material está diseñado para ser intuitivo y fácil de aplicar, tanto si recién empezás como si tenés años de experiencia.'
  }, {
    question: '¿Puedo usarlo en clases presenciales y virtuales?',
    answer: 'Absolutamente. Los archivos digitales son perfectos para compartir en pantalla, enviar por campus virtual o imprimir para el aula.'
  }, {
    question: '¿Cuánto tiempo tengo acceso?',
    answer: 'El acceso es DE POR VIDA. Pagás una sola vez y el material es tuyo para siempre, incluyendo futuras actualizaciones.'
  }, {
    question: '¿Es seguro comprar?',
    answer: 'Totalmente. Usamos pasarelas de pago encriptadas de nivel bancario. Nosotros no almacenamos tus datos de tarjeta.'
  }, {
    question: '¿Qué pasa si no me sirve?',
    answer: 'Tenés nuestra garantía de 7 días. Si el material no cumple tus expectativas, nos escribís y te devolvemos el 100% de tu dinero sin preguntas.'
  }];
  return <div className="min-h-screen bg-white font-sans text-gray-900 pb-20 md:pb-0" data-id="element-153">
      <MetaPixel data-id="element-154" />
      <PurchasePopup data-id="element-155" />
      <SocialProofBanner data-id="element-156" />
      <StickyCtaBar data-id="element-157" />

      {/* Urgency Banner */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-1.5 px-4 text-center sticky top-0 z-50 shadow-sm" data-id="element-158">
        <CountdownTimer variant="banner" data-id="element-159" />
      </div>

      {/* Hero Section */}
      <header className="bg-gradient-to-b from-emerald-50 to-white pt-8 pb-12 px-4 border-b border-gray-100" data-id="element-160">
        <div className="max-w-4xl mx-auto text-center" data-id="element-161">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-bold mb-4 animate-fade-in" data-id="element-162">
            <Users className="w-3 h-3" data-id="element-163" />
            <span data-id="element-164">
              Únete a 2,847+ docentes que ya transformaron su enseñanza
            </span>
          </div>

          <p className="text-emerald-700 font-bold tracking-wider uppercase mb-2 text-sm md:text-base" data-id="element-165">
            Para docentes de Biología
          </p>
          <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-4" data-id="element-166">
            ORGANIZÁ TODO TU AÑO DE BIOLOGÍA{' '}
            <span className="text-emerald-600" data-id="element-167">SIN ESTRÉS</span>
          </h1>

          <div className="mb-4 flex justify-center relative" data-id="element-168">
            <div className="absolute top-0 right-1/4 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-lg transform rotate-12 shadow-lg z-10 animate-bounce" data-id="element-169">
              -60% OFF
            </div>
            <img src={mainImage} alt="MegaPack Biología Lista - Colección Completa" className="w-full max-w-[300px] drop-shadow-2xl hover:scale-105 transition-transform duration-500" data-id="element-170" />
          </div>

          <p className="text-xl md:text-2xl text-gray-600 mb-6 max-w-2xl mx-auto" data-id="element-171">
            Material listo para usar que te ahorra horas de preparación y te
            permite dar clases claras, prácticas y alineadas al programa
            escolar.
          </p>

          <div className="flex flex-col items-center gap-4" data-id="element-172">
            <PurchaseButton text="QUIERO ACCESO INMEDIATO" redirectToCheckout={false} data-id="element-173" />
            <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-174">
              <span className="flex items-center gap-1" data-id="element-175">
                <Shield className="w-3 h-3 text-emerald-500" data-id="element-176" /> Compra segura
              </span>
              <span className="flex items-center gap-1" data-id="element-177">
                <Check className="w-3 h-3 text-emerald-500" data-id="element-178" /> Acceso de por
                vida
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Agitation / Pain Points */}
      <section className="py-16 px-4 bg-white" data-id="element-179">
        <div className="max-w-3xl mx-auto" data-id="element-180">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" data-id="element-181">
            Si sos docente de Biología, esto te va a sonar muy familiar…
          </h2>

          <div className="space-y-8 text-lg text-gray-700 leading-relaxed" data-id="element-182">
            <p data-id="element-183">
              Llegás a tu casa cansado/a… Sabés que tenés que planificar… Abrís
              la compu…{' '}
              <span className="font-bold text-red-500" data-id="element-184">
                Y empezás otra vez de cero.
              </span>
            </p>
            <p data-id="element-185">
              Buscando actividades. Buscando experiencias de laboratorio.
              Buscando evaluaciones. Buscando ejemplos que se entiendan.
              Buscando formas de explicar lo complejo sin complicarlo más.
            </p>
            <p className="italic border-l-4 border-green-500 pl-4 py-2 bg-green-50" data-id="element-186">
              Y el tiempo vuela. Y el cansancio crece. Y tu cabeza no para.
            </p>
            <p data-id="element-187">
              No porque no sepas enseñar. No porque no seas buen/a docente.{' '}
              <span className="font-bold" data-id="element-188">
                Sino porque estás intentando sostener todo el año sin un sistema
                que te sostenga a vos.
              </span>
            </p>
          </div>

          <div className="mt-16 bg-gray-50 p-8 rounded-2xl border border-gray-200" data-id="element-189">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-190">
              <AlertCircle className="text-red-500" data-id="element-191" />
              El problema real: hoy el sistema educativo te exige…
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-192">
              {['Planificar', 'Armar actividades', 'Preparar experiencias', 'Evaluar', 'Nivelar grupos', 'Preparar proyectos', 'Cumplir programas', 'Motivar alumnos', 'Registrar avances'].map(item => <div key={item} className="flex items-center gap-2 text-gray-700" data-id="element-193">
                  <div className="w-2 h-2 bg-red-400 rounded-full" data-id="element-194"></div>
                  {item}
                </div>)}
            </div>
            <p className="mt-6 text-center font-medium text-gray-900" data-id="element-195">
              …todo al mismo tiempo. Y nadie te dio un sistema que te ahorre ese
              trabajo.
            </p>
          </div>

          <div className="mt-12 text-center" data-id="element-196">
            <p className="text-xl text-gray-600 mb-6" data-id="element-197">
              Vivís improvisando, llevando trabajo a casa, corriendo detrás del
              programa y sintiendo que nunca terminás.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50" data-id="element-198">
        <div className="max-w-5xl mx-auto text-center" data-id="element-199">
          <span className="inline-block py-1 px-3 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold mb-6" data-id="element-200">
            LA SOLUCIÓN DEFINITIVA
          </span>
          <h2 className="text-4xl md:text-6xl font-black mb-8 text-gray-900" data-id="element-201">
            MEGAPACK BIOLOGÍA LISTA™
          </h2>
          <p className="text-xl md:text-2xl text-gray-700 mb-12 max-w-3xl mx-auto" data-id="element-202">
            Un sistema completo de materiales de Biología organizados por nivel
            para que nunca más tengas que empezar de cero.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12" data-id="element-203">
            {[{
            icon: Check,
            text: 'Todo listo'
          }, {
            icon: Shield,
            text: 'Todo probado'
          }, {
            icon: BookOpen,
            text: 'Todo ordenado'
          }, {
            icon: Zap,
            text: 'Todo usable'
          }].map((item, idx) => <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-emerald-100 flex flex-col items-center gap-3" data-id="element-204">
                <item.icon className="w-8 h-8 text-emerald-600" data-id="element-205" />
                <span className="font-bold text-lg text-gray-900" data-id="element-206">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 bg-white" data-id="element-207">
        <div className="max-w-4xl mx-auto" data-id="element-208">
          <div className="text-center mb-16" data-id="element-209">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-210">
              ¿Qué incluye el pack?
            </h2>
            <p className="text-xl text-gray-600" data-id="element-211">
              +600 páginas de material digital. Una biblioteca completa para
              todo tu año de Biología.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 items-center" data-id="element-212">
            <div className="order-2 md:order-1" data-id="element-213">
              <ul className="space-y-4" data-id="element-214">
                {['Guías de laboratorio listas', 'Teoría clara y secuenciada', 'Actividades prácticas', 'Evaluaciones y diagnósticos', 'Planillas docentes', 'Carpetas de campo', 'Proyectos anuales', 'Secuencias y planificaciones'].map((item, idx) => <li key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-215">
                    <div className="bg-emerald-100 p-1 rounded-full" data-id="element-216">
                      <Check className="w-5 h-5 text-emerald-600" data-id="element-217" />
                    </div>
                    <span className="font-medium text-gray-800" data-id="element-218">{item}</span>
                  </li>)}
              </ul>
              <div className="mt-8 p-4 bg-indigo-50 rounded-lg border border-indigo-100 text-center" data-id="element-219">
                <p className="text-indigo-800 font-bold" data-id="element-220">
                  Para todo el sistema educativo — TODOS LOS NIVELES
                </p>
              </div>
            </div>
            <div className="order-1 md:order-2 flex justify-center" data-id="element-221">
              <img src={mainImage} alt="Contenido del pack" className="w-full max-w-xs drop-shadow-xl" data-id="element-222" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-223">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-224">
          {/* Student Benefits */}
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200" data-id="element-225">
            <div className="flex items-center gap-3 mb-6" data-id="element-226">
              <Users className="w-8 h-8 text-blue-500" data-id="element-227" />
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-228">
                Qué van a lograr tus alumnos
              </h3>
            </div>
            <ul className="space-y-4" data-id="element-229">
              {['Entender la Biología paso a paso', 'Participar más en clase', 'Aprender haciendo', 'Comprender procesos complejos', 'Relacionar la Biología con la vida real', 'Sentirse más seguros'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-230">
                  <Check className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" data-id="element-231" />
                  <span className="text-gray-700" data-id="element-232">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* Teacher Benefits */}
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-emerald-200 ring-1 ring-emerald-100" data-id="element-233">
            <div className="flex items-center gap-3 mb-6" data-id="element-234">
              <Award className="w-8 h-8 text-emerald-600" data-id="element-235" />
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-236">
                Qué vas a lograr vos
              </h3>
            </div>
            <ul className="space-y-4" data-id="element-237">
              {['Ahorrar horas todas las semanas', 'Dejar de improvisar', 'Tener respaldo profesional', 'Cubrir todo el programa', 'Dar clases más claras y dinámicas', 'Volver a disfrutar enseñar'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-238">
                  <Check className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" data-id="element-239" />
                  <span className="text-gray-700 font-medium" data-id="element-240">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="mt-12 text-center" data-id="element-241">
          <PurchaseButton text="QUIERO LOGRAR ESTO" redirectToCheckout={false} data-id="element-242" />
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-20 px-4 bg-white" data-id="element-243">
        <div className="max-w-5xl mx-auto" data-id="element-244">
          <div className="text-center mb-16" data-id="element-245">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-246">
              BONOS EXTRA INCLUIDOS
            </h2>
            <p className="text-xl text-emerald-600 font-bold tracking-wide" data-id="element-247">
              GRATIS HOY CON TU COMPRA
            </p>
          </div>

          <div className="space-y-8" data-id="element-248">
            <BonusCard number={1} title="Biología Experimental" subtitle="+30 experiencias y actividades prácticas" description="Listas para aplicar desde la semana 1. Experiencias de laboratorio y aula que funcionan y atrapan a los alumnos." imageSrc={bonus1Image} data-id="element-249" />

            <BonusCard number={2} title="Kit de Evaluaciones por Año" subtitle="18 evaluaciones con criterios y rúbricas" description="Evaluaciones listas para 1° a 6°. Incluye criterios de corrección claros y rúbricas profesionales para ahorrarte tiempo corrigiendo." imageSrc={bonus2Image} data-id="element-250" />

            <BonusCard number={3} title="Material Didáctico Complementario" subtitle="Recursos visuales y teóricos extra" description="Genética, diversidad, metabolismo, ambiente y más. Láminas, esquemas y resúmenes para apoyar tus explicaciones." imageSrc={bonus3Image} data-id="element-251" />

            <BonusCard number={4} title="Planillas de Seguimiento y Planificación" subtitle="Tu organización docente resuelta" description="Modelos semanales y mensuales listos para organizarte y evaluar sin improvisar. Llevá el control de tus cursos fácilmente." imageSrc={bonus4Image} data-id="element-252" />
          </div>
        </div>
      </section>

      {/* FOMO Section */}
      <section className="py-16 px-4 bg-gray-900 text-white text-center" data-id="element-253">
        <div className="max-w-3xl mx-auto" data-id="element-254">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center justify-center gap-3" data-id="element-255">
            <AlertTriangle className="text-yellow-400 w-8 h-8" data-id="element-256" />
            Lo que pierdes si no actúas hoy:
          </h2>
          <div className="grid md:grid-cols-3 gap-6 text-left" data-id="element-257">
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700" data-id="element-258">
              <div className="text-red-400 font-bold text-lg mb-2" data-id="element-259">
                Pierdes Tiempo
              </div>
              <p className="text-gray-300 text-sm" data-id="element-260">
                Seguirás dedicando tus fines de semana a planificar en lugar de
                descansar.
              </p>
            </div>
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700" data-id="element-261">
              <div className="text-red-400 font-bold text-lg mb-2" data-id="element-262">
                Pierdes Dinero
              </div>
              <p className="text-gray-300 text-sm" data-id="element-263">
                Esta oferta del 60% OFF desaparece pronto. Mañana costará el
                doble.
              </p>
            </div>
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700" data-id="element-264">
              <div className="text-red-400 font-bold text-lg mb-2" data-id="element-265">
                Pierdes Paz
              </div>
              <p className="text-gray-300 text-sm" data-id="element-266">
                Continuarás con el estrés de no tener tus clases listas y
                organizadas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing / Offer Section */}
      <section id="compra" className="py-20 px-4 bg-gradient-to-br from-slate-50 via-gray-50 to-stone-50 relative overflow-hidden" data-id="element-267">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-5 pointer-events-none" data-id="element-268">
          <div className="absolute top-10 left-10 w-64 h-64 bg-emerald-400 rounded-full blur-3xl" data-id="element-269"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-400 rounded-full blur-3xl" data-id="element-270"></div>
        </div>

        <div className="max-w-xl mx-auto relative z-10 w-full" data-id="element-271">
          {/* Countdown Timer */}
          <div className="mb-8 text-center" data-id="element-272">
            <CountdownTimer className="justify-center mb-4" data-id="element-273" />
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gray-900" data-id="element-274">
              ACCESO COMPLETO DE POR VIDA
            </h2>
            <p className="text-xl text-gray-600" data-id="element-275">
              Todo el material + Todos los bonos
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-emerald-500 relative" data-id="element-276">
            {/* Best Value Badge */}
            <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-2 rounded-full font-bold shadow-lg uppercase tracking-wider text-sm whitespace-nowrap" data-id="element-277">
              🏆 Mejor Oferta Disponible
            </div>

            {/* Medal Icon */}
            <div className="flex justify-center mb-6 mt-4" data-id="element-278">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg" data-id="element-279">
                <Award className="w-8 h-8 text-white" data-id="element-280" />
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl md:text-3xl font-black text-center text-gray-900 mb-3" data-id="element-281">
              MEGAPACK BIOLOGÍA LISTA™
            </h3>
            <p className="text-center text-gray-600 mb-8 font-medium" data-id="element-282">
              Material completo para todo el año escolar
            </p>

            <ScarcityIndicator data-id="element-283" />

            {/* Subtle Scarcity Indicator */}
            <div className="flex items-center justify-center gap-2 mb-6 text-sm" data-id="element-284">
              <div className="flex items-center gap-1.5 text-gray-600" data-id="element-285">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" data-id="element-286"></div>
                <span className="font-medium" data-id="element-287">Quedan solo</span>
                <span className="font-bold text-red-600" data-id="element-288">3 cupos</span>
                <span className="font-medium" data-id="element-289">con este precio</span>
              </div>
            </div>

            {/* What's Included */}
            <div className="bg-emerald-50 rounded-2xl p-6 mb-6 border-2 border-emerald-100" data-id="element-290">
              <h4 className="text-center font-bold text-gray-900 mb-4 uppercase tracking-wide text-sm" data-id="element-291">
                ✅ Lo que recibes HOY:
              </h4>
              <div className="space-y-3" data-id="element-292">
                {['+600 páginas de material digital descargable', 'Guías de laboratorio listas para usar', 'Evaluaciones con rúbricas para 1° a 6°', 'Planillas de seguimiento y organización', 'Material didáctico complementario', 'Acceso inmediato y de por vida', '4 BONOS GRATIS (valor $47.960)'].map((feature, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-293">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-294" />
                    <span className="text-gray-800 font-medium text-sm" data-id="element-295">
                      {feature}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Pricing Section */}
            <div className="text-center mb-6" data-id="element-296">
              <div className="inline-block bg-red-100 border-2 border-red-300 rounded-xl p-4 mb-4" data-id="element-297">
                <p className="text-gray-500 line-through text-lg mb-1" data-id="element-298">
                  Precio regular: $49.990
                </p>
                <div className="flex items-center justify-center gap-2" data-id="element-299">
                  <span className="bg-red-600 text-white text-sm font-bold px-3 py-1 rounded-full animate-pulse" data-id="element-300">
                    60% OFF HOY
                  </span>
                  <span className="text-emerald-600 font-bold text-sm" data-id="element-301">
                    AHORRÁS $30.000
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-300 mb-4" data-id="element-302">
                <p className="text-gray-600 text-sm mb-2 font-semibold" data-id="element-303">
                  🎯 Precio especial de lanzamiento:
                </p>
                <div className="flex items-baseline justify-center gap-2 mb-2" data-id="element-304">
                  <span className="text-5xl md:text-6xl font-black text-emerald-600" data-id="element-305">
                    $19.990
                  </span>
                  <span className="text-xl font-bold text-gray-600" data-id="element-306">ARS</span>
                </div>
                <p className="text-gray-600 text-sm font-medium" data-id="element-307">
                  ✨ Pago único • Sin suscripciones • Tuyo para siempre
                </p>
              </div>
            </div>

            {/* Argentine Conversion Triggers */}

            {/* CTA Button */}
            <button onClick={() => window.location.href = 'https://packbiologia.recursospro.shop/cart/42847098896462:1'} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-black text-lg py-4 px-5 rounded-xl transition-all duration-200 shadow-[0_6px_0_rgb(5,150,105)] hover:shadow-[0_3px_0_rgb(5,150,105)] hover:translate-y-[3px] mb-4" data-id="element-308">
              <span className="flex items-center justify-center gap-2" data-id="element-309">
                <span className="text-xl" data-id="element-310">👉</span>
                SÍ, QUIERO MI MEGAPACK AHORA
                <span className="text-xl" data-id="element-311">🎯</span>
              </span>
            </button>

            <p className="text-center text-gray-500 text-xs mb-6 italic" data-id="element-312">
              💡 Acceso inmediato después del pago • Descargá todo en minutos
            </p>

            {/* MercadoPago Trust Section */}
            <div className="border-t-2 border-gray-100 pt-6" data-id="element-313">
              <p className="text-center text-gray-600 text-sm font-semibold mb-4 flex items-center justify-center gap-2" data-id="element-314">
                <Shield className="w-4 h-4 text-emerald-600" data-id="element-315" />
                Pago 100% seguro procesado por:
              </p>

              {/* Real MercadoPago Image */}
              <div className="flex justify-center mb-4" data-id="element-316">
                <img src="https://cdn.magicpatterns.com/uploads/1TFfWAVAYcMxNvbw5N1xKv/pasted-image.png" alt="MercadoPago" className="h-12 object-contain" data-id="element-317" />
              </div>

              {/* Payment Methods */}
              <div className="flex flex-wrap justify-center items-center gap-3 mb-4 opacity-70" data-id="element-318">
                <div className="flex items-center gap-1 font-bold text-blue-800 border border-gray-200 px-2 py-1 rounded bg-white text-xs" data-id="element-319">
                  <span className="italic" data-id="element-320">Visa</span>
                </div>
                <div className="flex items-center gap-1 font-bold text-red-600 border border-gray-200 px-2 py-1 rounded bg-white text-xs" data-id="element-321">
                  <span data-id="element-322">Mastercard</span>
                </div>
                <div className="flex items-center gap-1 font-bold text-orange-600 border border-gray-200 px-2 py-1 rounded bg-white text-xs" data-id="element-323">
                  <span data-id="element-324">Naranja</span>
                </div>
                <div className="flex items-center gap-1 font-bold text-purple-600 border border-gray-200 px-2 py-1 rounded bg-white text-xs" data-id="element-325">
                  <span data-id="element-326">Cabal</span>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-3 text-center text-xs" data-id="element-327">
                <div className="flex flex-col items-center gap-1" data-id="element-328">
                  <Shield className="w-5 h-5 text-emerald-600" data-id="element-329" />
                  <span className="text-gray-700 font-semibold" data-id="element-330">
                    Garantía
                    <br data-id="element-331" />7 días
                  </span>
                </div>
                <div className="flex flex-col items-center gap-1" data-id="element-332">
                  <Download className="w-5 h-5 text-emerald-600" data-id="element-333" />
                  <span className="text-gray-700 font-semibold" data-id="element-334">
                    Acceso
                    <br data-id="element-335" />
                    inmediato
                  </span>
                </div>
                <div className="flex flex-col items-center gap-1" data-id="element-336">
                  <Users className="w-5 h-5 text-emerald-600" data-id="element-337" />
                  <span className="text-gray-700 font-semibold" data-id="element-338">
                    +2,847
                    <br data-id="element-339" />
                    docentes
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Guarantee Section Below Card */}
          <div className="mt-8 bg-emerald-50 p-6 rounded-2xl border-2 border-emerald-200" data-id="element-340">
            <div className="flex items-start gap-4" data-id="element-341">
              <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-342">
                <Shield className="w-6 h-6 text-white" data-id="element-343" />
              </div>
              <div data-id="element-344">
                <p className="font-black text-gray-900 text-lg mb-2" data-id="element-345">
                  🛡️ GARANTÍA DE HIERRO - 7 DÍAS
                </p>
                <p className="text-gray-700 text-sm leading-relaxed" data-id="element-346">
                  Si no estás 100% satisfecho, te devolvemos tu dinero.{' '}
                  <strong data-id="element-347">Sin preguntas, sin vueltas.</strong> Probalo 7 días
                  completos y si no te sirve, te reintegramos todo. El riesgo es
                  nuestro, no tuyo.
                </p>
              </div>
            </div>
          </div>

          {/* Social Proof */}
          <div className="mt-6 text-center" data-id="element-348">
            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md border border-gray-200" data-id="element-349">
              <TrendingUp className="w-4 h-4 text-red-500" data-id="element-350" />
              <span className="text-sm text-gray-700" data-id="element-351">
                <strong className="text-red-600" data-id="element-352">47 docentes argentinos</strong>{' '}
                compraron hoy
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-353">
        <div className="max-w-6xl mx-auto" data-id="element-354">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4" data-id="element-355">
            Lo que dicen otros docentes
          </h2>
          <div className="flex justify-center gap-1 mb-12" data-id="element-356">
            {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-6 h-6 text-yellow-400 fill-current" data-id="element-357" />)}
            <span className="ml-2 text-gray-600 font-medium" data-id="element-358">
              4.9/5 basado en +2,000 reseñas
            </span>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-359">
            {testimonials.map((t, idx) => <div key={idx} className={idx >= 3 ? 'md:col-span-1 md:last:col-start-2' : ''} data-id="element-360">
                <TestimonialCard {...t} data-id="element-361" />
              </div>)}
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-16 px-4 bg-white" data-id="element-362">
        <div className="max-w-3xl mx-auto text-center border-2 border-dashed border-emerald-200 rounded-3xl p-8 md:p-12 bg-emerald-50/50" data-id="element-363">
          <div className="w-24 h-24 bg-white shadow-lg rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-emerald-100" data-id="element-364">
            <Shield className="w-12 h-12 text-emerald-600" data-id="element-365" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-366">
            Garantía de Satisfacción 100%
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-367">
            Tenés 7 días completos para probar todo el material. Descargalo,
            usalo en tus clases, revisalo a fondo. Si por alguna razón sentís
            que no vale 10 veces lo que pagaste, te devolvemos el 100% de tu
            dinero.
          </p>
          <div className="inline-block bg-white px-6 py-3 rounded-lg border border-gray-200 shadow-sm font-bold text-gray-800" data-id="element-368">
            Cero Riesgo para Vos
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-369">
        <div className="max-w-3xl mx-auto" data-id="element-370">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-371">
            PREGUNTAS FRECUENTES
          </h2>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8" data-id="element-372">
            {faqs.map((faq, idx) => <FAQItem key={idx} {...faq} data-id="element-373" />)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-white text-center" data-id="element-374">
        <div className="max-w-4xl mx-auto" data-id="element-375">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-8" data-id="element-376">
            QUIERO EMPEZAR AHORA
          </h2>
          <div className="mb-10" data-id="element-377">
            <img src={mainImage} alt="MegaPack Biología Lista" className="w-64 mx-auto drop-shadow-2xl hover:scale-110 transition-transform duration-300" data-id="element-378" />
          </div>
          <PurchaseButton redirectToCheckout={false} data-id="element-379" />
          <p className="mt-4 text-sm text-gray-500" data-id="element-380">
            Oferta válida por tiempo limitado
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 text-center text-sm mb-16 md:mb-0" data-id="element-381">
        <div className="max-w-4xl mx-auto" data-id="element-382">
          <p className="font-bold text-white text-lg mb-4" data-id="element-383">
            MEGAPACK BIOLOGÍA LISTA™
          </p>
          <p className="mb-6" data-id="element-384">Todos los derechos reservados © 2026</p>
          <div className="flex justify-center gap-6" data-id="element-385">
            <span className="hover:text-white cursor-pointer" data-id="element-386">Privacidad</span>
            <span className="hover:text-white cursor-pointer" data-id="element-387">Términos</span>
            <span className="hover:text-white cursor-pointer" data-id="element-388">Contacto</span>
          </div>
        </div>
      </footer>
    </div>;
}